/**
 * §B-7 station editor — Drawer create/edit (name / PrintNode printer id / scale
 * config JSON / active). The trust window is managed by the enroll/revoke actions
 * on the list, not here. Saves via the ADMIN API with optimistic concurrency.
 */
import { useState } from "react";
import { ApiError, api, type AdminStation, type StationInput } from "../../../lib/api.js";
import { Drawer, Field, Input, Button, useToast } from "../../../components/ui/index.js";

function parseJsonOrNull(text: string): { ok: true; value: Record<string, unknown> | null } | { ok: false } {
  const t = text.trim();
  if (!t) return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(t) as Record<string, unknown> };
  } catch {
    return { ok: false };
  }
}

export function StationEditor({ station, onClose, onSaved }: { station: AdminStation | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isNew = station === null;

  const [name, setName] = useState(station?.name ?? "");
  const [printNodeId, setPrintNodeId] = useState(station?.printNodeId != null ? String(station.printNodeId) : "");
  const [scaleText, setScaleText] = useState(station?.scaleConfig ? JSON.stringify(station.scaleConfig, null, 2) : "");
  const [active, setActive] = useState(station?.active ?? true);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return toast("Name is required.", "danger");
    const scale = parseJsonOrNull(scaleText);
    if (!scale.ok) return toast("Scale config JSON is not valid.", "danger");
    const pn = printNodeId.trim() ? Number(printNodeId) : null;
    if (pn !== null && (!Number.isInteger(pn) || pn <= 0)) return toast("PrintNode id must be a positive integer.", "danger");

    const input: StationInput = { name: name.trim(), printNodeId: pn, scaleConfig: scale.value, active };
    setSaving(true);
    try {
      if (isNew) await api.admin.createStation(input);
      else await api.admin.updateStation(station.id, input, station.updatedAt);
      toast(isNew ? "Station created" : "Station saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("This station changed since you opened it — reloading.", "danger");
        if (!isNew) onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid station.", "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required.", "danger");
      } else {
        toast("Couldn’t save the station.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      onClose={onClose}
      ariaLabel={isNew ? "New station" : `Edit ${station.name}`}
      title={isNew ? "Add station" : "Edit station"}
      meta={<span className="muted">Trust is granted via Enrol on the list, not here.</span>}
    >
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pack bench 1" autoFocus />
          </Field>
          <Field label="PrintNode printer id (optional)">
            <Input value={printNodeId} onChange={(e) => setPrintNodeId(e.target.value)} inputMode="numeric" placeholder="e.g. 740512" />
          </Field>
        </div>
        <Field label="Scale config JSON (optional)">
          <textarea
            className="cond__json control"
            rows={5}
            value={scaleText}
            onChange={(e) => setScaleText(e.target.value)}
            placeholder='{ "type": "serial", "protocol": "dymo", "pollCommand": "..." }'
            spellCheck={false}
            aria-label="Scale config JSON"
          />
        </Field>
        <Field label="Status">
          <label className="rule-form__toggle">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>{active ? "Active" : "Inactive"}</span>
          </label>
        </Field>

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Add station" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
